"use client";
import {
  createContext,
  ReactNode,
  startTransition,
  Suspense,
  useActionState,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  type Cart as CartData,
  CartType,
} from "@cappelendamm/e-commerce-services";
import { CartToast } from "./components/CartToast";
import { Cart as CartComponent } from "./components/Cart";
import { UNINITIALIZED_CART } from "./initial-cart";
import { cartActionReducer } from "./cartActionReducer";
import { CartSwapAndQueryParamHandler } from "./components/CartSwapAndQueryParamHandler";
import { useOptimisticCart } from "./useOptimisticCart";
import { CartTabSync } from "./components/CartTabSync";
import { EcommerceEvent } from "@/tracking/e-commerce";
import { useTrackEvent } from "@/tracking/TrackingContext";

export const CartContext = createContext<{
  cart: CartData | null;
  isPending: boolean;
  cartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  increaseQuantity: (event: {
    sku: string;
    quantity?: number;
  }) => Promise<void>;
  decreaseQuantity: (event: {
    sku: string;
    quantity?: number;
  }) => Promise<void>;
  setQuantity: (event: { sku: string; quantity: number }) => Promise<void>;
  removeItem: (event: { sku: string }) => Promise<void>;
  addItem: (event: { sku: string }) => Promise<void>;
  clearCart: () => Promise<void>;
  swapCart: (event: {
    targetCartType: CartType;
    skus?: string[];
  }) => Promise<void>;
  addVoucherCodeToCart?: (event: { voucherCode: string[] }) => Promise<void>;
  cartDisabled: boolean;
}>({
  cart: null,
  isPending: true,
  cartOpen: false,
  cartDisabled: false,
  openCart: () => {},
  closeCart: () => {},
  increaseQuantity: async () => {},
  decreaseQuantity: async () => {},
  setQuantity: async () => {},
  addItem: async () => {},
  removeItem: async () => {},
  clearCart: async () => {},
  swapCart: async () => {},
  addVoucherCodeToCart: async () => {},
});

export function CartProvider({
  children,
  experiments,
  cartDisabled = false,
}: {
  children: ReactNode;
  cartDisabled?: boolean;
  experiments?: {
    vippsInNetsForUnitSale: boolean;
  };
}) {
  const trackEvent = useTrackEvent();
  const [cartData, dispatchAction, isPending] = useActionState(
    cartActionReducer,
    UNINITIALIZED_CART,
  );

  const { optimisticCart, applyOptimistic } = useOptimisticCart(cartData);

  const [cartOpen, setCartOpen] = useState<boolean>(false);
  const cartRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    startTransition(async () => {
      dispatchAction({ type: "LOAD" });
    });
  }, [dispatchAction]);

  const addItem = async ({ sku }: { sku: string }) => {
    startTransition(async () => {
      applyOptimistic({ type: "INCREASE", sku });
      dispatchAction({ type: "ADD", sku, quantity: 1 });
      trackEvent(
        EcommerceEvent({
          eventName: "add_to_cart",
          skus: [sku],
          cartId: cartData.id,
          coupon: cartData.context?.price?.voucherCodes?.[0],
          quantity: 1,
        }),
      );
    });
  };

  const removeItem = async ({ sku }: { sku: string }) => {
    startTransition(async () => {
      applyOptimistic({ type: "DECREASE", sku, quantity: Infinity });
      dispatchAction({ type: "REMOVE", sku });
      trackEvent(
        EcommerceEvent({
          eventName: "remove_from_cart",
          skus: [sku],
          cartId: cartData.id,
          coupon: cartData.context?.price?.voucherCodes?.[0],
          quantity:
            cartData.items.find((item) => item.variant.sku === sku)?.quantity ??
            1,
        }),
      );
    });
  };

  const increaseQuantity = async ({
    sku,
    quantity = 1,
  }: {
    sku: string;
    quantity?: number;
  }) => {
    startTransition(() => {
      applyOptimistic({ type: "INCREASE", sku, quantity });
      dispatchAction({ type: "INCREASE", sku, quantity });
      trackEvent(
        EcommerceEvent({
          eventName: "add_to_cart",
          skus: [sku],
          cartId: cartData.id,
          coupon: cartData.context?.price?.voucherCodes?.[0],
          quantity,
        }),
      );
    });
  };

  const decreaseQuantity = async ({
    sku,
    quantity = 1,
  }: {
    sku: string;
    quantity?: number;
  }) => {
    startTransition(() => {
      applyOptimistic({ type: "DECREASE", sku, quantity });
      dispatchAction({ type: "DECREASE", sku, quantity });
      trackEvent(
        EcommerceEvent({
          eventName: "remove_from_cart",
          skus: [sku],
          cartId: cartData.id,
          coupon: cartData.context?.price?.voucherCodes?.[0],
          quantity,
        }),
      );
    });
  };

  const setQuantity = async ({
    sku,
    quantity,
  }: {
    sku: string;
    quantity: number;
  }) => {
    if (quantity <= 0) {
      return removeItem({ sku });
    }
    startTransition(() => {
      applyOptimistic({ type: "SET_QUANTITY", sku, quantity });
      dispatchAction({ type: "SET_QUANTITY", sku, quantity });
    });
  };

  const clearCart = useCallback(async () => {
    startTransition(async () => {
      applyOptimistic({ type: "CLEAR" });
      dispatchAction({ type: "CLEAR" });
    });
  }, [applyOptimistic, dispatchAction]);

  const swapCart = useCallback(
    async ({
      targetCartType,
      skus,
    }: {
      targetCartType: CartType;
      skus?: string[];
    }) => {
      startTransition(async () => {
        applyOptimistic({ type: "SWAP", targetCartType });
        dispatchAction({
          type: "SWAP",
          currentCartId: cartData.id,
          currentCartType: cartData.cartType,
          targetCartType,
          skus,
        });
      });
    },
    [cartData.id, cartData.cartType, dispatchAction],
  );

  const addVoucherCodeToCart = useCallback(
    async ({ voucherCode }: { voucherCode: string[] }) => {
      startTransition(async () => {
        dispatchAction({ type: "ADD_VOUCHER_CODE", voucherCode });
      });
    },
    [dispatchAction],
  );

  const openCart = useCallback(() => {
    if (cartRef.current) {
      cartRef.current?.showModal();
      trackEvent(
        EcommerceEvent({ eventName: "view_cart", cartId: cartData.id }),
      );
      setCartOpen(true);
    }
  }, [setCartOpen, trackEvent, cartData.id]);

  const closeCart = useCallback(() => {
    if (cartRef.current) {
      cartRef.current?.close();
      setCartOpen(false);
    }
  }, [setCartOpen]);

  return (
    <CartContext
      value={{
        cart: optimisticCart,
        isPending: isPending || cartData.id === UNINITIALIZED_CART.id,
        cartOpen,
        openCart,
        closeCart,
        addItem,
        removeItem,
        decreaseQuantity,
        increaseQuantity,
        setQuantity,
        clearCart,
        swapCart,
        addVoucherCodeToCart,
        cartDisabled,
      }}
    >
      {children}
      {!cartDisabled ? (
        <>
          <CartToast />
          <CartComponent
            ref={cartRef}
            vippsInNetsForUnitSale={experiments?.vippsInNetsForUnitSale ?? true}
          />
          <CartTabSync cartData={cartData} dispatchAction={dispatchAction} />
          {/* Suspense boundary required: CartSwapAndQueryParamHandler uses useSearchParams. */}
          <Suspense fallback={null}>
            <CartSwapAndQueryParamHandler />
          </Suspense>
        </>
      ) : null}
    </CartContext>
  );
}

export const useCart = () => useContext(CartContext);
